import { faEdit, faGavel } from "@fortawesome/free-solid-svg-icons";
import { type FC, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import EditCard from "src/components/editCard";
import { ErrorMessage, Icon, LoadingIndicator } from "src/components/fragments";
import ModalComponent from "src/components/modal";
import Title from "src/components/title";
import {
  EditOperationTypes,
  EditTargetTypes,
  ROUTE_EDIT_AMEND,
  ROUTE_EDIT_UPDATE,
} from "src/constants";
import {
  OperationEnum,
  useApproveEdit,
  useCancelEdit,
  useEdit,
  VoteStatusEnum,
} from "src/graphql";
import { useCurrentUser } from "src/hooks";
import {
  createHref,
  getEditDetailsName,
  getEditTargetName,
  getEditTargetRoute,
} from "src/utils";
import DeleteEditModal from "./components/DeleteEditModal";
import { UpdateCount } from "./components/UpdateCount";

const EditComponent: FC = () => {
  const { isAdmin, isModerator, isSelf } = useCurrentUser();
  const { id } = useParams();
  const [showApply, setShowApply] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const { data, loading } = useEdit({ id: id ?? "" }, !id);
  const [cancelEdit, { loading: canceling }] = useCancelEdit();
  const [approveEdit, { loading: applying }] = useApproveEdit();
  const { hash } = useLocation();

  // Comments render only after the edit query resolves, so the browser's
  // native hash scroll misses the anchor - scroll once it's in the DOM.
  useEffect(() => {
    if (!data?.findEdit || !hash) return;
    const el = document.getElementById(hash.slice(1));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data?.findEdit, hash]);

  if (loading) return <LoadingIndicator message="Loading..." />;

  const edit = data?.findEdit;
  if (!edit) return <ErrorMessage error="Failed to load edit." />;

  const toggleCancelModal = () => setShowCancel(true);
  const toggleApplyModal = () => setShowApply(true);

  const handleCancel = (status: boolean): void => {
    if (status) cancelEdit({ variables: { input: { id: edit.id } } });
    setShowCancel(false);
  };
  const handleApprove = (status: boolean): void => {
    if (status)
      approveEdit({ variables: { input: { id: edit.id } } }).then((result) => {
        const target = result.data?.approveEdit.target;
        if (!target) return;

        window.location.href = `${getEditTargetRoute(target)}#edits`;
      });
    setShowApply(false);
  };

  const cancelModal = showCancel && (
    <ModalComponent
      message="Are you sure you want to cancel this edit?"
      callback={handleCancel}
      acceptTerm="Yes, cancel edit"
      cancelTerm="Cancel"
    />
  );

  const approveModal = showApply && (
    <ModalComponent
      message="Are you sure you want to approve this edit?"
      callback={handleApprove}
      acceptTerm="Approve edit"
    />
  );

  const mutating = canceling || applying;

  // Update Edit is owner-only (and admin via implies). Cancel Edit is
  // available to the owner, any moderator, or admin (see Edit.Cancel in
  // internal/service/edit/service.go). Approve Edit is moderator-only.
  const isPending = edit.status === VoteStatusEnum.PENDING;
  const isOwner = isAdmin || isSelf(edit.user?.id);
  const canCancel = isOwner || isModerator;

  const buttons = isPending && (isOwner || isModerator) && (
    <div className="d-flex justify-content-end">
      {isOwner && (
        <>
          <UpdateCount
            updatable={edit.updatable}
            updateCount={edit.update_count}
          />
          {edit.updatable && (
            <Link to={createHref(ROUTE_EDIT_UPDATE, edit)} className="me-2">
              <Button variant="primary" disabled={mutating}>
                Update Edit
              </Button>
            </Link>
          )}
        </>
      )}
      {canCancel && (
        <Button
          variant="danger"
          className="me-2"
          disabled={showCancel || mutating}
          onClick={toggleCancelModal}
        >
          Cancel Edit
        </Button>
      )}
      {isModerator && (
        <Button
          variant="danger"
          disabled={showApply || mutating}
          onClick={toggleApplyModal}
        >
          Approve Edit
        </Button>
      )}
    </div>
  );

  const modButtons = isModerator && edit.closed && (
    <div className="d-flex justify-content-end mb-2">
      <Link to={createHref(ROUTE_EDIT_AMEND, edit)} className="me-2">
        <Button variant="primary">
          <Icon icon={faEdit} className="me-2" />
          Amend Edit
        </Button>
      </Link>
      <Button variant="danger" onClick={() => setShowDelete(true)}>
        <Icon icon={faGavel} className="me-2" />
        Delete Edit
      </Button>
    </div>
  );

  const deleteModal = showDelete && (
    <DeleteEditModal
      edit={edit}
      show={showDelete}
      onHide={() => setShowDelete(false)}
    />
  );

  const targetName =
    edit.operation === OperationEnum.CREATE
      ? getEditDetailsName(edit.details)
      : getEditTargetName(edit.target);

  return (
    <div>
      <Title
        page={`${EditOperationTypes[edit.operation]} ${
          EditTargetTypes[edit.target_type]
        } "${targetName}"`}
      />
      {modButtons}
      <EditCard edit={edit} showVotes />
      {buttons}
      {cancelModal}
      {approveModal}
      {deleteModal}
    </div>
  );
};

export default EditComponent;
